import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { AdministrationAreaGate } from '@/admin-area/AdministrationAreaGate';
import { InvitationSheet } from '@/admin-area/invitations/InvitationSheet';
import { OrganizationSheet } from '@/admin-area/organizations/OrganizationSheet';
import { OrganizationsPage } from '@/admin-area/organizations/OrganizationsPage';
import { OldTenantRedirect } from '@/admin-area/place/old-addresses';
import { RoleCatalogPage } from '@/admin-area/role-catalog/RoleCatalogPage';
import { PlatformTenantsPage } from '@/admin-area/tenants/PlatformTenantsPage';
import { TenantSheet } from '@/admin-area/tenants/TenantSheet';
import { RoleSheet } from '@/admin-area/role-catalog/RoleSheet';
import { SummaryPage } from '@/admin-area/summary/SummaryPage';
import { InvitationsPage } from '@/admin-area/invitations/InvitationsPage';
import { ModuleSheet } from '@/admin-area/modules/ModuleSheet';
import { ModulesPage } from '@/admin-area/modules/ModulesPage';
import { PeoplePage } from '@/admin-area/people/PeoplePage';
import { PersonSheet } from '@/admin-area/people/PersonSheet';
import { AdministrationScopesProvider } from '@/admin-area/place/AdministrationScopesProvider';
import {
  AdministrationLanding,
  PlaceIndex,
  PlaceLayout,
  PlaceSection,
  PlatformLayout,
} from '@/admin-area/place/PlaceLayout';
import { PlatformHome } from '@/admin-area/platform/PlatformHome';
import { RolesPage } from '@/admin-area/roles/RolesPage';
import { ActiveContextProvider } from '@/auth/ActiveContextProvider';
import { AuthProvider, useAuth } from '@/auth/AuthProvider';
import { LoginPage } from '@/auth/login-page';
import { OperatorProvider } from '@/auth/OperatorProvider';
import { ClinicalStudyGate } from '@/clinical-study/ClinicalStudyGate';
import { StudyOversightEntryPage } from '@/clinical-study/oversight/StudyOversightEntryPage';
import { StudyOversightPage } from '@/clinical-study/oversight/StudyOversightPage';
import { OperationalQuickReferencePage } from '@/clinical-study/quick-reference/OperationalQuickReferencePage';
import { ClinicalStudiesPage } from '@/clinical-study/studies/ClinicalStudiesPage';
import { ClinicalStudyDetailPage } from '@/clinical-study/studies/ClinicalStudyDetailPage';
import { ParticipantCalendarPage } from '@/clinical-study/subjects/ParticipantCalendarPage';
import { TreatmentDatePoolPage } from '@/clinical-study/treatment-date-pool/TreatmentDatePoolPage';
import { AcceptanceGate } from '@/consent/AcceptanceGate';
import { FinanceDashboardPage } from '@/finance/FinanceDashboardPage';
import { FinanceCashFlowPage } from '@/finance/FinanceCashFlowPage';
import { FinanceCommessePage } from '@/finance/FinanceCommessePage';
import { FinanceIncomeStatementPage } from '@/finance/FinanceIncomeStatementPage';
import { FinanceReferencePage } from '@/finance/FinanceReferencePage';
import { FinanceDividendsPage } from '@/finance/FinanceDividendsPage';
import { FinancePrimaNotaPage } from '@/finance/prima-nota/FinancePrimaNotaPage';
import { FinanceGate } from '@/finance/FinanceGate';
import { ArchiveGate } from '@/archive/ArchiveGate';
import { ARCHIVE_OVERVIEW_READ, ARCHIVE_RULES_READ } from '@/archive/access';
import { OverviewPage as ArchiveOverviewPage } from '@/archive/overview/OverviewPage';
import { RulesPage as ArchiveRulesPage } from '@/archive/rules/RulesPage';
import { CommunicationsGate } from '@/communications/CommunicationsGate';
import { ApprovalsPage as CommunicationsApprovalsPage } from '@/communications/approvals/ApprovalsPage';
import { LogPage as CommunicationsLogPage } from '@/communications/log/LogPage';
import { UnsubscribePage, unsubscribeTokenOf } from '@/communications/unsubscribe/UnsubscribePage';
import { ExpensesGate } from '@/expenses/ExpensesGate';
import { CaptureExpensePage } from '@/expenses/capture/CaptureExpensePage';
import { ExpenseDetailPage } from '@/expenses/detail/ExpenseDetailPage';
import { ImportReferencePage } from '@/expenses/import/ImportReferencePage';
import { ClosingPage } from '@/expenses/closing/ClosingPage';
import { OwnerDetailsPeoplePage } from '@/expenses/people/OwnerDetailsPeoplePage';
import { ExpensesIndex } from '@/expenses/ExpensesIndex';
import { PurposesPage } from '@/expenses/purposes/PurposesPage';
import { NoteTypesPage } from '@/expenses/purposes/NoteTypesPage';
import { ReservedPurposesPage } from '@/expenses/reserved/ReservedPurposesPage';
import { MileageSettingsPage } from '@/expenses/mileage/MileageSettingsPage';
import { FlatRateSettingsPage } from '@/expenses/flat-rate/FlatRateSettingsPage';
import { RoutesPage } from '@/expenses/mileage/RoutesPage';
import { TripPage } from '@/expenses/mileage/TripPage';
import { VehiclesPage } from '@/expenses/mileage/VehiclesPage';
import { ApprovalsPage } from '@/expenses/approvals/ApprovalsPage';
import { ReportPage } from '@/expenses/reports/ReportPage';
import { ReportsListPage } from '@/expenses/reports/ReportsListPage';
import { ToolsGate } from '@/tools/ToolsGate';
import { CLEAN_TEXT_AVAILABLE } from '@/tools/clean-text/availability';
import { CleanTextPage } from '@/tools/clean-text/CleanTextPage';
import { DeviceTestPage } from '@/tools/device-test/DeviceTestPage';
import { IdentityDocumentPage } from '@/tools/identity-documents/IdentityDocumentPage';
import { ImagesPage } from '@/tools/images/ImagesPage';
import { PdfPage } from '@/tools/pdf/PdfPage';
import { ToolsPage } from '@/tools/ToolsPage';
import { ProfilePage } from '@/profile/ProfilePage';
import { GuidePage } from '@/service-pages/GuidePage';
import { ServiceStatusPage } from '@/service-pages/ServiceStatusPage';
import { Shell } from '@/shell/Shell';
import { StagingBar } from '@/shell/StagingBar';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { DashboardPage } from './routes/dashboard-page';
import { StartScreen } from './start/StartScreen';
import { resetStartProgress } from './start/start-progress';

/** Catalogo ruoli, at every place: read only, for everyone who administers it, with its role card. */
const roleCatalogRoute = (prefix = '') => ({
  path: `${prefix}role-catalog`,
  element: (
    <PlaceSection id="role-catalog">
      <RoleCatalogPage />
    </PlaceSection>
  ),
  children: [{ path: ':roleId', element: <RoleSheet /> }],
});

/** The sections built so far, each shown only where the place has it for this caller. */
function placeSections(prefix = '') {
  return [
    {
      path: `${prefix}overview`,
      element: (
        <PlaceSection id="overview">
          <SummaryPage />
        </PlaceSection>
      ),
    },
    {
      path: `${prefix}organizations`,
      element: (
        <PlaceSection id="organizations">
          <OrganizationsPage />
        </PlaceSection>
      ),
      children: [{ path: ':organizationId', element: <OrganizationSheet /> }],
    },
    roleCatalogRoute(prefix),
    {
      path: `${prefix}people`,
      element: (
        <PlaceSection id="people">
          <PeoplePage />
        </PlaceSection>
      ),
      children: [{ path: ':userId', element: <PersonSheet /> }],
    },
    {
      path: `${prefix}roles`,
      element: (
        <PlaceSection id="roles">
          <RolesPage />
        </PlaceSection>
      ),
    },
    {
      path: `${prefix}invitations`,
      element: (
        <PlaceSection id="invitations">
          <InvitationsPage />
        </PlaceSection>
      ),
      children: [{ path: ':invitationId', element: <InvitationSheet /> }],
    },
    {
      path: `${prefix}modules`,
      element: (
        <PlaceSection id="modules">
          <ModulesPage />
        </PlaceSection>
      ),
      children: [{ path: ':moduleId', element: <ModuleSheet /> }],
    },
  ];
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'profile', element: <ProfilePage /> },
      // The footer's two service pages (`shell/footer/service-links.ts`), open to every signed-in
      // User like the profile: placeholders until their content is written.
      { path: 'guide', element: <GuidePage /> },
      { path: 'status', element: <ServiceStatusPage /> },
      {
        // Identity documents moved into Strumenti (#387); the old address keeps working.
        path: 'identity-documents',
        element: <Navigate to="/tools/identity-documents" replace />,
      },
      {
        // «Strumenti» (#387): `tools:use` in the active organization (decision 100); the server
        // keeps its own guards either way.
        path: 'tools',
        element: <ToolsGate />,
        children: [
          { index: true, element: <ToolsPage /> },
          { path: 'images', element: <ImagesPage /> },
          { path: 'pdf', element: <PdfPage /> },
          ...(CLEAN_TEXT_AVAILABLE ? [{ path: 'clean-text', element: <CleanTextPage /> }] : []),
          { path: 'identity-documents', element: <IdentityDocumentPage /> },
          { path: 'device-test', element: <DeviceTestPage /> },
        ],
      },
      {
        // «Note spese» (release 1): capture and list. `new` is declared before `:expenseId` and
        // wins on specificity anyway; the finance service keeps its own guards either way.
        path: 'expenses',
        element: <ExpensesGate />,
        children: [
          { index: true, element: <ExpensesIndex /> },
          // Release 2: the reports. Declared before `:expenseId`, so `reports` is never an id.
          { path: 'approvals', element: <ApprovalsPage /> },
          { path: 'closings', element: <ClosingPage /> },
          { path: 'reports', element: <ReportsListPage /> },
          { path: 'reports/:reportId', element: <ReportPage /> },
          { path: 'new', element: <CaptureExpensePage /> },
          // Release 3: mileage. Static segments, so none is read as an `:expenseId`.
          { path: 'trips/new', element: <TripPage /> },
          { path: 'trips/:expenseId/edit', element: <TripPage /> },
          { path: 'vehicles', element: <VehiclesPage /> },
          { path: 'routes', element: <RoutesPage /> },
          { path: 'settings/mileage', element: <MileageSettingsPage /> },
          { path: 'settings/flat-rate', element: <FlatRateSettingsPage /> },
          { path: 'settings/purposes', element: <PurposesPage /> },
          { path: 'settings/note-types', element: <NoteTypesPage /> },
          { path: 'settings/reserved-purposes', element: <ReservedPurposesPage /> },
          { path: 'people', element: <OwnerDetailsPeoplePage /> },
          { path: 'import', element: <ImportReferencePage /> },
          { path: ':expenseId', element: <ExpenseDetailPage /> },
          // The same capture page, in edit mode (it reads `expenseId`).
          { path: ':expenseId/edit', element: <CaptureExpensePage /> },
        ],
      },
      {
        // «Archivio» (#788): the overview and the rules, each behind its own read permission.
        path: 'archive',
        children: [
          {
            element: <ArchiveGate permission={ARCHIVE_OVERVIEW_READ} />,
            children: [{ index: true, element: <ArchiveOverviewPage /> }],
          },
          {
            element: <ArchiveGate permission={ARCHIVE_RULES_READ} />,
            children: [{ path: 'rules', element: <ArchiveRulesPage /> }],
          },
        ],
      },
      {
        // «Comunicazioni» (#739): the Director approves the onboarding emails and reads the log.
        path: 'communications',
        element: <CommunicationsGate />,
        children: [
          { index: true, element: <Navigate to="approvals" replace /> },
          { path: 'approvals', element: <CommunicationsApprovalsPage /> },
          { path: 'log', element: <CommunicationsLogPage /> },
        ],
      },
      {
        path: 'finance',
        element: <FinanceGate />,
        // The pathless layout that used to sit here carried a tab bar over these five views. The
        // shell's two-level rail carries them now, from the module catalog, and `SectionTabs`
        // renders the same list in the one width band where the rail is out of the flow — so the
        // layout had nothing left to hold and the views are the gate's own children again.
        children: [
          { index: true, element: <FinanceDashboardPage /> },
          { path: 'conto-economico', element: <FinanceIncomeStatementPage /> },
          { path: 'flusso-di-cassa', element: <FinanceCashFlowPage /> },
          { path: 'commesse', element: <FinanceCommessePage /> },
          { path: 'dati-di-riferimento', element: <FinanceReferencePage /> },
          { path: 'dividendi', element: <FinanceDividendsPage /> },
          { path: 'prima-nota', element: <FinancePrimaNotaPage /> },
        ],
      },
      {
        path: 'admin',
        children: [
          // Today's operator screens are retired (FR-AD-12): their addresses lead to the new places.
          // `tenants/:t/organizations/:o` needs no redirect: it is now the organization's card.
          { path: 'tenants', element: <Navigate to="/admin/platform/tenants" replace /> },
          { path: 'tenants/:tenantId', element: <OldTenantRedirect /> },
          // The administration area by place (ADR-018, decisions 60 and 61). A tenant's sections
          // are listed one by one under `tenants/:placeId/`; `tenants/:tenantId` alone redirects.
          {
            element: <AdministrationAreaGate />,
            children: [
              // `/admin` opens on a place (FR-AD-1).
              { index: true, element: <AdministrationLanding /> },
              {
                path: 'platform',
                element: <PlatformLayout />,
                children: [
                  { index: true, element: <PlatformHome /> },
                  {
                    path: 'tenants',
                    element: (
                      <PlaceSection id="tenants">
                        <PlatformTenantsPage />
                      </PlaceSection>
                    ),
                    children: [{ path: ':tenantId', element: <TenantSheet /> }],
                  },
                  roleCatalogRoute(),
                ],
              },
              {
                path: 'organizations/:placeId',
                element: <PlaceLayout kind="organization" />,
                children: [{ index: true, element: <PlaceIndex /> }, ...placeSections()],
              },
              {
                element: <PlaceLayout kind="tenant" />,
                children: placeSections('tenants/:placeId/'),
              },
            ],
          },
        ],
      },
      {
        path: 'clinical-study',
        element: <ClinicalStudyGate />,
        children: [
          { index: true, element: <Navigate to="studies" replace /> },
          { path: 'studies', element: <ClinicalStudiesPage /> },
          { path: 'studies/:studyId', element: <ClinicalStudyDetailPage /> },
          {
            path: 'studies/:studyId/subjects/:subjectId/participant-calendar',
            element: <ParticipantCalendarPage />,
          },
          { path: 'treatment-date-pool', element: <TreatmentDatePoolPage /> },
          { path: 'quick-reference', element: <OperationalQuickReferencePage /> },
          { path: 'oversight', element: <StudyOversightEntryPage /> },
          { path: 'oversight/:studyId', element: <StudyOversightPage /> },
        ],
      },
    ],
  },
]);

/**
 * Gates the shell on auth state (ADR-008): signed-out shows LoginPage, signed-in shows the shell.
 * `AcceptanceGate` (SL-0030), `ActiveContextProvider` (SL-0015), and `OperatorProvider` (SL-0023)
 * only mount once signed in — all three depend on an authenticated `/v1` call. `AcceptanceGate`
 * wraps `ActiveContextProvider` (Resolution 8) — the first-access policy-acceptance gate blocks
 * before any other authenticated `/v1` call renders its own UI.
 */
function AuthGate() {
  const { status, emailLinkOutcome } = useAuth();
  // The start screen's first step (#430): Firebase restoring the session.
  if (status === 'loading') {
    return <StartScreen step={0} />;
  }

  if (status === 'completing-email-link') {
    // The email-link return leg (SL-0045 Task 3.1): a neutral completing state, never the idle
    // LoginPage — except the one sub-state that NEEDS user input, the cross-device
    // 'email-required' re-prompt, which lives on LoginPage's email-link block (Task 3.2; the
    // reconciliation of the two clauses is recorded in SL-0045 § Divergences).
    if (emailLinkOutcome === 'email-required') {
      return <LoginPage />;
    }
    return <StartScreen step={0} />;
  }

  if (status === 'signed-out') {
    // A sign-in from here starts the bar again from 0, even after an earlier start in this load.
    resetStartProgress();
    return <LoginPage />;
  }

  return (
    <AcceptanceGate>
      <ActiveContextProvider>
        <OperatorProvider>
          <AdministrationScopesProvider>
            <RouterProvider router={router} />
          </AdministrationScopesProvider>
        </OperatorProvider>
      </ActiveContextProvider>
    </AcceptanceGate>
  );
}

export function App() {
  // The unsubscribe link of an onboarding email works WITHOUT signing in (#739): it sits outside
  // the auth gate and the shell, and the token in the link is the only proof it needs.
  const unsubscribeToken = unsubscribeTokenOf(window.location.pathname);
  if (unsubscribeToken !== null) {
    return (
      <ThemeProvider>
        <UnsubscribePage token={unsubscribeToken} />
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider>
      <StagingBar />
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </ThemeProvider>
  );
}
